import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, PaginatedResponse } from "@/api/api";
import { useSuspenseQuery, useInfiniteQuery } from "@tanstack/react-query";
import { SensorKitAlert } from "@/alerts/alert";

export interface AlertLog {
  alert_type: string;
  time: string;
}

export interface AlertLogFilters {
  alert_id?: number;
  page?: number;
  pageSize?: number;
}

export interface SensorKitAlertFilters {
  sensor_kit_offline?: string;
  sensor_kit_missing_data?: string;
  sensor_kit_out_of_spec?: string;
  page?: number;
  pageSize?: number;
}

// Shared query function for sensor kit alerts
const buildSensorKitAlertsUrl = (filters: Partial<SensorKitAlertFilters>) => {
  let url = `/sensor-kit-alerts/`;
  const params = new URLSearchParams();

  if (filters?.sensor_kit_offline) {
    params.append('sensor_kit_offline', filters.sensor_kit_offline);
  }

  if (filters?.sensor_kit_missing_data) {
    params.append('sensor_kit_missing_data', filters.sensor_kit_missing_data);
  }

  if (filters?.sensor_kit_out_of_spec) {
    params.append('sensor_kit_out_of_spec', filters.sensor_kit_out_of_spec);
  }

  if (filters?.page !== undefined) {
    params.append('page', filters.page.toString());
  }

  if (filters?.pageSize !== undefined) {
    params.append('page_size', filters.pageSize.toString());
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return url;
};

export const sensorKitAlerts = createQueryKeys('sensor-kit-alerts', {
  detail: (
    alertId: number,
  ) => ({
    queryKey: [alertId],
    queryFn: async () => {
      return await get<SensorKitAlert>(`sensor-kit-alerts/${alertId}/`);
    },
  }),
  list: (filters?: SensorKitAlertFilters) => ({
    queryKey: ["list", filters],
    queryFn: async () => {
      const url = buildSensorKitAlertsUrl(filters || {});
      return await get<PaginatedResponse<SensorKitAlert>>(url);
    },
  }),
  infinite: (filters?: Omit<SensorKitAlertFilters, 'page' | 'pageSize'>) => ({
    queryKey: ["infinite", filters],
  })
});

export const useSensorKitAlert = (alertId: number) => {
  return useSuspenseQuery(sensorKitAlerts.detail(alertId))
}

export const useSensorKitAlerts = (filters?: SensorKitAlertFilters) => {
  return useSuspenseQuery(sensorKitAlerts.list(filters))
}

// Shared query function for alert logs
const buildAlertLogsUrl = (filters: Partial<AlertLogFilters>) => {
  let url = `/alert-logs/`;
  const params = new URLSearchParams();

  if (filters?.alert_id !== undefined) {
    params.append('alert_id', filters.alert_id.toString());
  }

  if (filters?.page !== undefined) {
    params.append('page', filters.page.toString());
  }

  if (filters?.pageSize !== undefined) {
    params.append('page_size', filters.pageSize.toString());
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return url;
};

export const alertLogs = createQueryKeys('alert-logs', {
  list: (filters?: AlertLogFilters) => ({
    queryKey: ["list", filters],
    queryFn: async () => {
      const url = buildAlertLogsUrl(filters || {});
      return await get<PaginatedResponse<AlertLog>>(url);
    },
  }),
  infinite: (filters?: Omit<AlertLogFilters, 'page' | 'pageSize'>) => ({
    queryKey: ["infinite", filters],
  })
});

export const useAlertLogs = (filters?: AlertLogFilters) => {
  return useSuspenseQuery(alertLogs.list(filters))
}

// Infinite query for alert logs (loads most recent first, then "load previous")
export const useAlertLogsInfinite = (filters?: Omit<AlertLogFilters, 'page' | 'pageSize'>) => {
  return useInfiniteQuery({
    queryKey: alertLogs.infinite(filters).queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const url = buildAlertLogsUrl({
        ...filters,
        page: pageParam as number,
        pageSize: 10
      });
      return await get<PaginatedResponse<AlertLog>>(url);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResponse<AlertLog>, allPages) => {
      // Check if there are more pages
      const totalPages = Math.ceil(lastPage.count / 10);
      const currentPage = allPages.length;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
}

// Infinite query for sensor kit alerts
export const useSensorKitAlertsInfinite = (filters?: Omit<SensorKitAlertFilters, 'page' | 'pageSize'>) => {
  return useInfiniteQuery({
    queryKey: sensorKitAlerts.infinite(filters).queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const url = buildSensorKitAlertsUrl({
        ...filters,
        page: pageParam as number,
        pageSize: 10
      });
      return await get<PaginatedResponse<SensorKitAlert>>(url);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResponse<SensorKitAlert>, allPages) => {
      // Check if there are more pages
      const totalPages = Math.ceil(lastPage.count / 10);
      const currentPage = allPages.length;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
}